import React, { useMemo, useState } from "react";
import {
  Table,
  Text,
  Box,
  Group,
  UnstyledButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconSelector,
} from "@tabler/icons-react";
import { ReportColumn, ReportDefinition } from "../../types/reports";
import { formatCellForDisplay } from "../../utils/reportUtils";
import { StandardTableShell } from "../common/StandardTable";
import styles from "./ReportsPage.module.css";

interface Props {
  definition: ReportDefinition;
  rows: Array<Record<string, unknown>>;
  hiddenColumns: string[];
  sortField: string | null;
  sortDirection: "asc" | "desc";
  onSort: (column: string) => void;
}

// Columns that should never wrap, even though they're typed `string`.
// Names, statuses, and IDs are all single-token values where wrapping
// looks broken in a narrow column. The `items_sold` roll-up is
// deliberately omitted so a long comma-separated list of services /
// products can still wrap and not blow up the row height.
const NOWRAP_STRING_KEYS = new Set([
  "client_name",
  "transaction_status",
  "appointment_status",
  "reference_id",
  "staff",
  // Package & Specials Sales string columns — keep every column on one line
  // (the table scrolls horizontally) so the report reads like the others.
  "kind",
  "name",
  "package_type",
  "effective_window",
]);

function isNoWrapCol(col: ReportColumn): boolean {
  if (col.type !== "string") return true;
  return NOWRAP_STRING_KEYS.has(col.key);
}

export default function ReportTable({
  definition,
  rows,
  hiddenColumns,
  sortField,
  sortDirection,
  onSort,
}: Props) {
  const visibleColumns: ReportColumn[] = definition.columns.filter(
    (c) => !hiddenColumns.includes(c.key),
  );
  // Either form of breakdown reserves the same chevron column at the
  // left of the table — the only difference is which row field it
  // expands from (`staff_breakdown` vs `line_items`).
  const supportsBreakdown =
    definition.supports_staff_breakdown ||
    definition.supports_line_item_breakdown;
  const breakdownField: "staff_breakdown" | "line_items" =
    definition.supports_line_item_breakdown ? "line_items" : "staff_breakdown";
  const groupingCol =
    visibleColumns.find((c) => c.is_grouping) ?? visibleColumns[0];
  // The cell on a child row that shows the child's display name.
  // Staff breakdowns put the staff member's name in the grouping
  // column; line-item children put the line description under the
  // parent's "items_sold" column so they visually align with the
  // Services Provided / Products Sold roll-up.
  const childNameCol: ReportColumn | undefined =
    breakdownField === "line_items"
      ? (visibleColumns.find((c) => c.key === "items_sold") ?? groupingCol)
      : groupingCol;

  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const toggleRow = (id: string) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Totals: sum every currency / int column over the visible parent
  // rows. Skipped when the report has supports_totals=false (e.g. cash
  // drawer sessions, where summing across rows isn't meaningful).
  const totalsRow = useMemo<Record<string, number> | null>(() => {
    if (!definition.supports_totals) return null;
    if (rows.length === 0) return null;
    const totals: Record<string, number> = {};
    let hasNumeric = false;
    for (const col of visibleColumns) {
      if (col.type !== "currency" && col.type !== "int") continue;
      hasNumeric = true;
      let sum = 0;
      for (const row of rows) {
        const v = row[col.key];
        if (typeof v === "number") sum += v;
      }
      totals[col.key] = sum;
    }
    return hasNumeric ? totals : null;
  }, [rows, visibleColumns, definition.supports_totals]);

  if (rows.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Text c="dimmed">No data for the selected filters.</Text>
      </Box>
    );
  }

  return (
    <StandardTableShell ariaLabel="Report">
      <Table.Thead>
        <Table.Tr>
          {supportsBreakdown && <Table.Th style={{ width: 40 }} />}
          {visibleColumns.map((col) => {
            const isSorted = sortField === col.key;
            const Icon = !isSorted
              ? IconSelector
              : sortDirection === "asc"
                ? IconChevronUp
                : IconChevronDown;
            const align =
              col.type === "currency" || col.type === "int" ? "right" : "left";
            const header = (
              <UnstyledButton
                className={styles.sortableHeader}
                onClick={() => onSort(col.key)}
              >
                <Group gap={4} wrap="nowrap" justify={align}>
                  <Text fw={600} size="sm">
                    {col.label}
                  </Text>
                  <Icon size={14} opacity={isSorted ? 1 : 0.5} />
                </Group>
              </UnstyledButton>
            );
            return (
              <Table.Th key={col.key} style={{ textAlign: align }}>
                {col.description ? (
                  <Tooltip label={col.description} multiline w={220} withArrow>
                    {header}
                  </Tooltip>
                ) : (
                  header
                )}
              </Table.Th>
            );
          })}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, idx) => {
          // Row identity: prefer a stable id (so React reconciliation
          // survives sort/filter), but fall back to a unique idx-based
          // key the data layer can never collide with.
          const stableId =
            (row.id as string | undefined) ??
            (row.staff_id as string | undefined) ??
            (row.sale_date as string | undefined) ??
            null;
          const rowKey = stableId ?? `__row-${idx}`;
          const breakdown = ((row as Record<string, unknown>)[breakdownField] ??
            []) as Array<Record<string, unknown>>;
          const isExpanded = expandedRowIds.has(rowKey);
          const canExpand = supportsBreakdown && breakdown.length > 0;
          return (
            <React.Fragment key={rowKey}>
              <Table.Tr
                onClick={canExpand ? () => toggleRow(rowKey) : undefined}
                className={
                  canExpand && isExpanded ? styles.parentExpanded : undefined
                }
                style={canExpand ? { cursor: "pointer" } : undefined}
              >
                {supportsBreakdown && (
                  <Table.Td style={{ width: 40 }}>
                    {canExpand && (
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRow(rowKey);
                        }}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? (
                          <IconChevronDown size={14} />
                        ) : (
                          <IconChevronRight size={14} />
                        )}
                      </ActionIcon>
                    )}
                  </Table.Td>
                )}
                {visibleColumns.map((col) => {
                  const align =
                    col.type === "currency" || col.type === "int"
                      ? "right"
                      : "left";
                  const noWrap = isNoWrapCol(col);
                  return (
                    <Table.Td
                      key={col.key}
                      style={{
                        textAlign: align,
                        whiteSpace: noWrap ? "nowrap" : undefined,
                      }}
                    >
                      {formatCellForDisplay(
                        row[col.key],
                        col.type,
                        col.accounting_negative,
                      )}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
              {canExpand &&
                isExpanded &&
                breakdown.map((b, i) => {
                  // Line-item children carry their own stable id; staff
                  // breakdown children key on staff_id. Fall back to the
                  // index when neither is available so the React key is
                  // still unique.
                  const childId =
                    (b.line_item_id as string | undefined) ??
                    (b.staff_id as string | undefined) ??
                    String(i);
                  return (
                    <Table.Tr
                      key={`${rowKey}-child-${childId}`}
                      className={styles.expandRow}
                    >
                      <Table.Td style={{ width: 40 }} />
                      {visibleColumns.map((col) => {
                        const align =
                          col.type === "currency" || col.type === "int"
                            ? "right"
                            : "left";
                        const noWrap = isNoWrapCol(col);
                        const isNameSlot = col === childNameCol;
                        if (isNameSlot) {
                          // Staff breakdown rows store the name in
                          // `staff_member`; line-item rows store the
                          // description in the items_sold column key.
                          const display =
                            (b[col.key] as string | undefined) ??
                            (b.staff_member as string | undefined) ??
                            "—";
                          return (
                            <Table.Td
                              key={col.key}
                              style={{
                                textAlign: align,
                                paddingLeft: 32,
                                whiteSpace: noWrap ? "nowrap" : undefined,
                              }}
                            >
                              <Text size="sm" c="dimmed">
                                {String(display)}
                              </Text>
                            </Table.Td>
                          );
                        }
                        const value = b[col.key];
                        // Line-item children blank out the
                        // parent-only columns (sale_dt, client_name,
                        // transaction_status, reference_id) by sending
                        // null. Render those as empty cells rather
                        // than the "—" filler we use for staff
                        // breakdowns, since on Transaction Log those
                        // columns are conceptually inherited from the
                        // parent row above.
                        const blankFiller =
                          breakdownField === "line_items" ? "" : "—";
                        return (
                          <Table.Td
                            key={col.key}
                            style={{
                              textAlign: align,
                              whiteSpace: noWrap ? "nowrap" : undefined,
                            }}
                          >
                            {value === undefined || value === null
                              ? blankFiller
                              : formatCellForDisplay(
                                  value,
                                  col.type,
                                  col.accounting_negative,
                                )}
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  );
                })}
            </React.Fragment>
          );
        })}
        {totalsRow && (
          <Table.Tr className={styles.totalsRow}>
            {supportsBreakdown && <Table.Td style={{ width: 40 }} />}
            {visibleColumns.map((col) => {
              const align =
                col.type === "currency" || col.type === "int"
                  ? "right"
                  : "left";
              const isNameSlot = col === groupingCol;
              if (isNameSlot) {
                return (
                  <Table.Td key={col.key} style={{ textAlign: align }}>
                    <Text fw={700}>Total</Text>
                  </Table.Td>
                );
              }
              if (!(col.key in totalsRow)) {
                return <Table.Td key={col.key} />;
              }
              const noWrap = isNoWrapCol(col);
              return (
                <Table.Td
                  key={col.key}
                  style={{
                    textAlign: align,
                    whiteSpace: noWrap ? "nowrap" : undefined,
                  }}
                >
                  <Text fw={700}>
                    {formatCellForDisplay(
                      totalsRow[col.key],
                      col.type,
                      col.accounting_negative,
                    )}
                  </Text>
                </Table.Td>
              );
            })}
          </Table.Tr>
        )}
      </Table.Tbody>
    </StandardTableShell>
  );
}
